import { useState } from "react";
import { Button, SlidePanel } from "@/components/ui";
import { useInstallPrompt } from "../../hooks/useInstallPrompt.js";

const DISMISS_KEY = "pwa:install-dismissed-at";
/** Descartar no es "nunca más": a los 30 días se vuelve a ofrecer. */
const SNOOZE_MS = 30 * 24 * 60 * 60 * 1000;

function wasRecentlyDismissed() {
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    return Date.now() - Number(raw) < SNOOZE_MS;
  } catch {
    // Safari en modo privado puede lanzar al tocar localStorage.
    return false;
  }
}

const IOS_STEPS = [
  { icon: "ios_share", text: "Toca el botón Compartir en la barra de Safari." },
  { icon: "add_box", text: 'Desplázate y elige "Añadir a pantalla de inicio".' },
  { icon: "check_circle", text: 'Confirma con "Añadir" arriba a la derecha.' },
];

/**
 * InstallPrompt — Invitación a instalar la app.
 *
 * Dos caminos según plataforma:
 *   - Android/Chrome: hay prompt nativo, se dispara desde nuestro botón.
 *   - iOS/Safari: no existe API de instalación, solo se puede explicar el flujo
 *     manual. No es cosmético — iOS únicamente entrega web push a PWAs
 *     instaladas, así que sin este paso el iPhone no recibe alertas.
 */
export default function InstallPrompt() {
  const { canPrompt, needsIOSHint, promptInstall } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(() => wasRecentlyDismissed());
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  const visible = !dismissed && (canPrompt || needsIOSHint);

  function dismiss() {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // Sin persistencia el banner reaparece en la próxima sesión; aceptable.
    }
  }

  return (
    <>
      {visible && (
        <div className="fixed left-4 right-4 z-[600] bottom-[calc(5rem+env(safe-area-inset-bottom))] md:left-auto md:right-4 md:bottom-4 md:max-w-sm flex items-center gap-3 px-4 py-3 rounded-xl border border-border bg-surface-1 sheen-2 shadow-panel">
          <span
            className="material-symbols-outlined shrink-0 text-primary"
            style={{ fontSize: "20px" }}
          >
            install_mobile
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-text-primary">Instalar GPS Platform</p>
            <p className="text-xs text-text-muted leading-snug">
              Ábrela como app y recibe alertas en el celular.
            </p>
          </div>
          <Button
            size="sm"
            onClick={() => (canPrompt ? promptInstall() : setShowIOSGuide(true))}
          >
            {canPrompt ? "Instalar" : "Cómo"}
          </Button>
          <button
            type="button"
            aria-label="Descartar"
            onClick={dismiss}
            className="shrink-0 text-text-dim hover:text-text-primary transition-colors"
          >
            <span className="material-symbols-outlined" style={{ fontSize: "18px" }}>
              close
            </span>
          </button>
        </div>
      )}

      <SlidePanel
        isOpen={showIOSGuide}
        title="Instalar en iPhone"
        size="sm"
        onClose={() => setShowIOSGuide(false)}
      >
        <div className="p-4 flex flex-col gap-4">
          <p className="text-sm text-text-muted">
            En iPhone la instalación se hace desde Safari. Es necesaria para recibir
            notificaciones: iOS no las entrega mientras la app viva solo en una pestaña.
          </p>
          <ol className="flex flex-col gap-3">
            {IOS_STEPS.map((step, i) => (
              <li key={step.icon} className="flex items-start gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center">
                  {i + 1}
                </span>
                <span className="material-symbols-outlined text-text-muted shrink-0" style={{ fontSize: "20px" }}>
                  {step.icon}
                </span>
                <p className="text-sm text-text-primary leading-snug">{step.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </SlidePanel>
    </>
  );
}
